import { formatPhoneNumber } from '@klareospace/pharos-lib-front-utils';
import { buildCustomerDefaultContact, formatPostalAddress, normalizeCountry } from '@klareospace/pharos-lib-utils';
import type { Customer, CustomerAddress, PhoneNumber, PostalAddress, Site } from '@klareospace/pharos-lib-types';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import {
  buildAddressEntryFromCustomerAddress,
  findDefaultAddressIndex,
  normalizeDefaultAddresses,
} from '&/features/customers';
import type { AddressBookEntryInput } from '&/features/customers';

import type { RouteInformationFormInput, RouteInformationTimeSlotFormInput } from '../schemas/order-creation.schema';
import { buildDefaultTimeSlotValue, isDateRangeValueInverted } from './time-slot.utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RouteInformationContact {
  name: string;
  phone: string;
}

interface ResolvePreselectedSiteParams {
  /** `useMasterContext().siteId` — wins over every other rule when set. */
  masterSiteId: string | null;
  /** `useUserScopes(user).singleSiteId` — the one site a site-scoped user has access to. */
  singleSiteId: string | null;
  sites: Site[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolves the delivery contact: name and phone each independently prefer the address entry's
 * own override and otherwise fall back to the customer's own name/phone — so a partial override
 * (only a name, or only a phone) is still honored instead of being discarded in favor of the
 * customer.
 */
export function resolveDeliveryContact(
  entry: AddressBookEntryInput,
  customer: Customer | null,
): RouteInformationContact {
  const customerContact = customer ? buildCustomerDefaultContact(customer) : null;
  const override = resolveContactOverride(entry);

  const name = override.name || customerContact?.name || '';
  const phoneSource = override.phone ?? customerContact?.phone;
  const phone = phoneSource ? formatPhoneNumber(phoneSource) : '';

  return { name, phone };
}

/**
 * Formats a delivery/pickup address for display, normalizing the country to its full display
 * name first — `formatPostalAddress` only resolves alpha-3 codes on its own, so a stored alpha-2
 * code (or a legacy full name in a different casing) would otherwise render as-is instead of the
 * canonical name the customers address cards show.
 */
export function formatRouteAddress(address: Partial<PostalAddress> | undefined): string {
  if (!address) return '';
  return formatPostalAddress({ ...address, country: normalizeCountry(address.country ?? '') });
}

/** True when an enabled pickup timeslot has start >= end; a disabled or empty timeslot is never inverted. */
export function isPickupTimeSlotInverted(timeSlot: RouteInformationTimeSlotFormInput): boolean {
  if (!timeSlot.enabled || !timeSlot.value) return false;
  return isDateRangeValueInverted(timeSlot.value);
}

/**
 * Seeds a fresh pickup timeslot value when the checkbox is enabled with no prior value — preset
 * `'custom'`, since the range is seeded from "now", not a calendar day (per the epic owner's
 * ruling, the pickup timeslot uses the same picker as the Orders page date filter). See
 * `buildDefaultTimeSlotValue` (`./time-slot.utils`) for the rounding/duration/clamp rules shared
 * with the Scheduling section's delivery timeslot.
 */
export function buildDefaultPickupTimeSlotValue(site: Site | null): DateRangeValue {
  return buildDefaultTimeSlotValue(site, 'custom');
}

/**
 * Rebuilds the customer's full `addresses` array for the PATCH address-book sync: replaces the
 * edited entry at `sourceIndex`, or appends it in add mode, then re-normalizes "exactly one
 * default" the same way the customers feature does (first flagged entry stays default; none
 * flagged ⇒ the first entry becomes default).
 */
export function mergeCustomerAddress(
  existing: CustomerAddress[],
  sourceIndex: number | undefined,
  built: CustomerAddress,
): CustomerAddress[] {
  const merged =
    sourceIndex !== undefined
      ? existing.map((address, index) => (index === sourceIndex ? built : address))
      : [...existing, built];
  return normalizeDefaultAddresses(merged);
}

/**
 * Complete iff a pickup site is selected AND a delivery address is present AND (the pickup
 * timeslot is disabled OR enabled with a valid start < end range).
 */
export function isRouteInformationComplete(route: RouteInformationFormInput): boolean {
  if (!route.selectedSite || !route.deliveryAddress) return false;
  if (!route.pickupTimeSlot.enabled) return true;
  if (!route.pickupTimeSlot.value) return false;
  return !isPickupTimeSlotInverted(route.pickupTimeSlot);
}

/**
 * Builds the Delivery pre-fill entry from a customer's preferred address (`location.isDefault`,
 * falling back to the first address), or `null` for a customer with no saved address yet. Single
 * source of the default-address rule — shared by the pre-fill effect (`useOrderCreationForm`) and
 * the customer-select handler's completion prediction (`OrderCreationDrawer`), so neither can drift
 * from the other.
 */
export function buildDeliveryAddressEntry(customer: Customer): RouteInformationFormInput['deliveryAddress'] {
  const resolvedIndex = findDefaultAddressIndex(customer.addresses);
  const preferred = customer.addresses[resolvedIndex];
  if (!preferred) return null;
  return buildAddressEntryFromCustomerAddress(preferred, resolvedIndex, customer);
}

/**
 * The single source for the Route information section-level error i18n key (timeslot-specific vs
 * generic) — used both by `validateRouteInformation` (on Next) and the drawer's auto-completion
 * effect, so a stale message never outlives the condition that produced it. Returns the key, not
 * the translated string, so callers resolve it through their own `useTranslation('order-creation')`.
 */
export function resolveRouteInformationErrorMessageKey(route: RouteInformationFormInput): string {
  if (route.selectedSite && route.deliveryAddress && isPickupTimeSlotInverted(route.pickupTimeSlot)) {
    return 'sections.routeInformation.invalidRange';
  }
  return 'sections.routeInformation.incomplete';
}

/**
 * Pickup pre-selection: master-selector site wins when set; else the single site a site-scoped
 * user has access to; else the one site left when exactly one is in the user's scoped `sites`
 * result; else left empty for the user to pick.
 */
export function resolvePreselectedSite({
  masterSiteId,
  singleSiteId,
  sites,
}: ResolvePreselectedSiteParams): Site | null {
  const findSite = (id: string | null): Site | null => (id ? (sites.find((site) => site.id === id) ?? null) : null);
  return findSite(masterSiteId) ?? findSite(singleSiteId) ?? (sites.length === 1 ? sites[0] : null);
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

interface ContactOverride {
  name?: string;
  phone?: PhoneNumber | null;
}

/** The entry's own contact override — empty when `isCustomerContact` is true (no override stored). */
function resolveContactOverride(entry: AddressBookEntryInput): ContactOverride {
  if (entry.isCustomerContact) return {};
  return { name: entry.contactName?.trim(), phone: entry.contactPhone };
}
